// src/store/slices/userSlice.js
import { createSlice } from '@reduxjs/toolkit';
import userService from '../../services/userService';
import storageHelper from '../../utils/storageHelper';

// Initial state
const initialState = {
  userDetails: {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    country: '',
    language: '',
    business_plans_count: 0,
    market_validations_count: 0,
    team_members_count: 0,
    subscription_status: '',
    subscription_price: 0,
    profile_photo_url: '',
    affiliation_id: null
  },
  isLoading: false,
  error: null,
  lastFetched: null // Add timestamp for last successful fetch
};

// Create the slice
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setUserDetails: (state, action) => {
      state.userDetails = action.payload;
      state.lastFetched = Date.now();
    },
    updateUserDetails: (state, action) => {
      state.userDetails = { ...state.userDetails, ...action.payload };
      state.lastFetched = Date.now();
    },
    clearUserError: (state) => {
      state.error = null;
    },
    clearUserProfile: (state) => {
      state.userDetails = initialState.userDetails;
      state.error = null;
      state.isLoading = false;
      state.lastFetched = null;
    },
    logout: () => initialState
  }
});

// Export actions
export const { setLoading, setError, setUserDetails, updateUserDetails, clearUserError, logout, clearUserProfile } = userSlice.actions;

// Export selectors
export const selectUserDetails = (state) => state.user.userDetails;
export const selectUserLoading = (state) => state.user.isLoading;
export const selectUserError = (state) => state.user.error;
export const selectLastFetched = (state) => state.user.lastFetched;

// Helper function to check if we should refresh the profile
const shouldRefreshProfile = (lastFetched) => {
  if (!lastFetched) return true;
  const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
  return Date.now() - lastFetched > REFRESH_INTERVAL;
};

// Action creator for fetching user profile
export const fetchUserProfile =
  (force = false) =>
  async (dispatch, getState) => {
    const authToken = storageHelper.get('authToken');
    const lastFetched = selectLastFetched(getState());

    if (!authToken) {
      dispatch(clearUserProfile());
      return;
    }

    // Skip if recently fetched unless forced
    if (!force && !shouldRefreshProfile(lastFetched)) {
      return;
    }

    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const response = await userService.getUserDetails();

      if (response) {
        dispatch(setUserDetails(response));
      }
    } catch (err) {
      console.error('Profile fetch error:', err);
      dispatch(setError(err.message || 'Failed to fetch user profile'));

      if (err.status === 401) {
        storageHelper.remove('authToken');
        dispatch(clearUserProfile());
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

// Action creator for updating user profile
export const updateUserProfile = (userData) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));

    const response = await userService.updateUserDetails(userData);

    if (response) {
      dispatch(updateUserDetails(response));
    }

    return response;
  } catch (err) {
    console.error('Profile update error:', err);
    dispatch(setError(err.message || 'Failed to update user profile'));

    if (err.status === 401) {
      storageHelper.remove('authToken');
      dispatch(clearUserProfile());
    }
    throw err;
  } finally {
    dispatch(setLoading(false));
  }
};

// Thunk for handling authentication state changes
export const handleAuthStateChange = (token) => (dispatch) => {
  if (token) {
    // localStorage.setItem('authToken', token);
    storageHelper.set('authToken', token);
    dispatch(fetchUserProfile(true)); // Force fetch on new token
  } else {
    // localStorage.removeItem('authToken');
    storageHelper.remove('authToken');
    dispatch(clearUserProfile());
  }
};

// Export reducer
export default userSlice.reducer;
